<template>
    <AppModal ref="modal" :title="$t('labels.duplicate_product')">
        <div class="modal-body">
            <FormGroup :label="this.$t(`labels.name`) + ` (${this.$t(`shortLang.${lang}`)})`" v-for="lang in integration.language_aliases">
                <Input v-model="product.names[lang]" readonly/>
            </FormGroup>
            <FormGroup :label="this.$t(`labels.sku`)">
                <Input v-model="product.sku"/>
            </FormGroup>
            <div v-if="Object.values(filters).length > 0" class="table-responsive">
                <table class="table table-bordered table-fixed">
                    <thead>
                        <tr>
                            <th width="50%">{{ $t('labels.filter') }}</th>
                            <th width="50%">{{ $t('labels.filter_item') }}</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="filter in filters">
                            <td>{{ filter.filterName }}</td>
                            <td>
                                <AppAutoComplete
                                    v-if="!filter.isMultiple"
                                    :url="`/projects/${$route.params.projectUuid}/pim/channels/${$route.params.integrationUuid}/filter-items`"
                                    :model-value="filter.items[0]?.uuid"
                                    :selected-value="filter.items[0]?.names[integration.language_aliases[0]]"
                                    @update:model-value="filter.items[0] = $event"
                                    option-key="uuid"
                                    option-label="name"
                                    :query-params="{
                                        integration_filter_uuids: [filter.filterUuid]
                                    }"
                                />
                                <MultiAutoComplete
                                    v-else
                                    :url="`/projects/${$route.params.projectUuid}/pim/channels/${$route.params.integrationUuid}/filter-items`"
                                    v-model="filter.items"
                                    option-key="uuid"
                                    option-label="name"
                                    :query-params="{
                                        integration_filter_uuids: [filter.filterUuid]
                                    }"
                                />
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>
        <div class="modal-footer d-flex justify-content-end">
            <AppButton color="indigo" @click="duplicate()" :disabled="!product.sku">
                {{ $t('buttons.duplicate') }}
            </AppButton>
        </div>
    </AppModal>
</template>
<script>
import AppModal from "../../../../../../../components/new/modal/AppModal.vue";
import AppButton from "../../../../../../../components/new/button/AppButton.vue";
import FormGroup from "../../../../../../../components/new/form/group/FormGroup.vue";
import Input from "../../../../../../../components/new/form/Input.vue";
import AppAutoComplete from "../../../../../../../components/new/form/autocomplete/AppAutoComplete.vue";
import MultiAutoComplete from "../../../../../../../components/new/form/autocomplete/MultiAutoComplete.vue";

import { httpClient } from "../../../../../../../services/HttpClient";
import MessageNotificator from "../../../../../../../services/MessageNotificator";

export default {
    name: "DuplicateProductModal",
    components: {
        AppModal, AppButton, FormGroup, Input, AppAutoComplete, MultiAutoComplete
    },
    props: {
        integration: {
            type: Object,
            required: true
        }
    },
    data() {
        return {
            product: {},
            filters: {},
            notVariableFilterItems: []
        };
    },
    methods: {
        async show(product) {
            this.product = JSON.parse(JSON.stringify(product));
            this.product.sku = null;
            this.filters = {};
            
            if (product.group_key) {
                await this.loadGroupProductFilters(product.group_key);
            } else {
                this.loadSingleProductFilters(this.product.filters);
            }

            this.$refs.modal.show();
        },
        async loadGroupProductFilters(groupKey) {
            const groupProducts = await this.fetchGroupProducts(groupKey);
            const filterValuesByUuidByProduct = await this.getFilterValuesByUuidByProduct(groupProducts);
            const availableFilters = this.getGroupFilters(groupProducts);
            const filterValuesList = Object.values(filterValuesByUuidByProduct);
            this.notVariableFilterItems = this.getNotVariableFilterItems(groupProducts);

            for (const filter of Object.values(availableFilters)) {
                if (!filterValuesList[0]?.[filter.filterUuid]) {
                    this.createEmptyFilter(filter);
                    continue;
                }

                const firstProductValue = filterValuesList[0][filter.filterUuid];
                const allProductsHaveSameValue = filterValuesList.every(
                    productFilters => productFilters[filter.filterUuid] === firstProductValue
                );

                if (!allProductsHaveSameValue) {
                    this.createEmptyFilter(filter);
                }
            }
        },
        loadSingleProductFilters(productFilters) {
            for (const filter of productFilters) {
                if (!this.filters.hasOwnProperty(filter.uuid)) {
                    this.filters[filter.uuid] = {
                        filterName: filter.names[this.integration.language_aliases[0]],
                        filterUuid: filter.uuid,
                        items: [filter.item]
                    };
                    continue;
                }

                this.filters[filter.uuid].items.push(filter.item);
            }
        },
        createEmptyFilter(filter) {
            this.filters[filter.filterUuid] = {
                filterName: filter.filterName,
                filterUuid: filter.filterUuid,
                isMultiple: filter.isMultiple,
                items: []
            };
        },
        hide() {
            this.$refs.modal.hide();
        },
        async getFilterValuesByUuidByProduct(groupProducts) {
            const filterValuesByUuidByProduct = {};

            for (const product of groupProducts) {
                filterValuesByUuidByProduct[product.uuid] = {};

                for (const filterItem of product.integration_filter_items) {
                    const filterUuid = filterItem.integration_filter.uuid;
                    const itemId = String(filterItem.id);

                    if (!filterValuesByUuidByProduct[product.uuid].hasOwnProperty(filterUuid)) {
                        filterValuesByUuidByProduct[product.uuid][filterUuid] = itemId;
                    } else {
                        filterValuesByUuidByProduct[product.uuid][filterUuid] += `-${itemId}`;
                    }
                }
            }

            return filterValuesByUuidByProduct;
        },
        async fetchGroupProducts(groupKey) {
            const limit = 100;
            const products = [];

            const firstResponse = await httpClient.get(`/integrations/${this.integration.uuid}/products`, {
                group_keys: [groupKey],
                extra_fields: ['integrationFilterItems'],
                page: 1,
                limit
            });
            products.push(...firstResponse.data.data);

            const totalPages = Math.ceil(firstResponse.data.meta.total / limit);
            for (let page = 2; page <= totalPages; page++) {
                const response = await httpClient.get(`/integrations/${this.integration.uuid}/products`, {
                    group_keys: [groupKey],
                    extra_fields: ['integrationFilterItems'],
                    page,
                    limit
                });
                products.push(...response.data.data);
            }

            return products;
        },
        getGroupFilters(groupProducts) {
            const groupFilters = {};

            for (const product of groupProducts) {
                for (const filterItem of product.integration_filter_items) {
                    const filterUuid = filterItem.integration_filter.uuid;
                    
                    if (!groupFilters.hasOwnProperty(filterUuid)) {
                        groupFilters[filterUuid] = {
                            filterName: filterItem.integration_filter.names[this.integration.language_aliases[0]],
                            filterUuid: filterUuid,
                            isMultiple: filterItem.integration_filter.is_multiple
                        };
                    }
                }
            }
                
            return groupFilters;
        },
        getNotVariableFilterItems(groupProducts) {
            if (!Array.isArray(groupProducts) || !groupProducts.length) {
                return [];
            }

            const getProductFilterData = (product) => {
                const filters = {};
                
                for (const item of (product.integration_filter_items || [])) {
                    const { uuid: filterUuid, is_multiple } = item.integration_filter;
                    
                    if (!filters[filterUuid]) {
                        filters[filterUuid] = {
                            isMultiple: is_multiple,
                            itemUuids: new Set()
                        };
                    }
                    filters[filterUuid].itemUuids.add(item.uuid);
                }
                return filters;
            };

            let commonFilters = getProductFilterData(groupProducts[0]);

            for (let i = 1; i < groupProducts.length; i++) {
                const currentFilters = getProductFilterData(groupProducts[i]);

                for (const filterUuid in commonFilters) {
                    const candidate = commonFilters[filterUuid];
                    const current = currentFilters[filterUuid];

                    const isMatch = current && 
                        candidate.itemUuids.size === current.itemUuids.size && 
                        [...candidate.itemUuids].every(uuid => current.itemUuids.has(uuid));

                    if (!isMatch) {
                        delete commonFilters[filterUuid];
                    }
                }
            }

            return Object.entries(commonFilters).flatMap(([filterUuid, { isMultiple, itemUuids }]) => {
                const items = [...itemUuids];
                const itemsToInclude = isMultiple ? items : items.slice(0, 1);
                
                return itemsToInclude.map(itemUuid => ({ filterUuid, itemUuid }));
            });
        },
        async duplicate() {
            let requestData = {
                names: Object.fromEntries(
                    Object.entries(this.product.names).filter(([_, value]) => value)
                ),
                integration_category_uuid: this.product.category.uuid,
                integration_uuid: this.integration.uuid,
                export_enabled: this.product.export_enabled
            }

            if (this.product.images.length) {
                requestData.images = this.product.images;
            }

            if (Object.values(this.product.descriptions).filter(Boolean).length) {
                requestData.descriptions = Object.fromEntries(
                    Object.entries(this.product.descriptions).filter(([_, value]) => value)
                );
            }

            if (Object.values(this.product.seos).filter(Boolean).length) {
                requestData.seos = Object.fromEntries(
                    Object.entries(this.product.seos).filter(([_, value]) => value)
                );
            }

            if (this.product.sku) {
                requestData.sku = this.product.sku;
            }

            if (this.product.measurement) {
                requestData.measurement = this.product.measurement;
            }

            if (this.product.manufacturer) {
                requestData.manufacturer_name = this.product.manufacturer;
            }

            if (this.product.balance) {
                requestData.balance = this.product.balance;
            }

            if (this.product.price) {
                requestData.price = this.product.price;
            }

            if (this.product.sale_price) {
                requestData.sale_price = this.product.sale_price;
            }

            if (this.product.promo_price) {
                requestData.promo_price = this.product.promo_price;
            }

            if (this.product.group_key) {
                requestData.group_key = this.product.group_key;
            }

            if (Object.keys(this.filters).length) {
                requestData.filter_item_uuids = Object.values(this.filters).reduce((acc, filter) => {
                    filter.items.forEach(item => {
                        acc.push(item.uuid);
                    });
                    return acc;
                }, []);
            }

            if (this.notVariableFilterItems.length) {
                if (!requestData.filter_item_uuids) {
                    requestData.filter_item_uuids = [];
                }
                
                requestData.filter_item_uuids.push(...this.notVariableFilterItems.map(item => item.itemUuid)); 
            }

            await httpClient.post('/integrations/products', requestData).then(() => {
                this.hide();
                new MessageNotificator().success(this.$t('alerts.success'));
            });
        }
    }
};
</script>